import { PartyType, ResidenceType } from "@/modules/transaction/common/enum";
import {
  BUDGET_ACCOUNT_PREFIX,
  CARD_TRANSFER_MAX_AMOUNT,
  CARD_TRANSFER_MIN_AMOUNT,
  INPUT_MAX_LIMIT,
  MAX_AMOUNT,
  MESSAGE_VALIDATION,
  ONLY_DIGITS,
  TEXT_FIELD_VALIDATION_REGEX,
} from "@/shared/common/constants";
import { ErrorMessages } from "@/shared/common/enums";
import type {
  ValidationSchemaAccountTransferProps,
  ValidationSchemaBeneficiaryListProps,
  ValidationSchemaBudgetTransferProps,
  ValidationSchemaCardTransferProps,
  ValidationSchemaGroupTransferProps,
  ValidationSchemaProps,
  ValidationSchemaReportCodeProps,
} from "@/shared/common/types";
import { cleanupAmount, isAmeriaAccount } from "@/shared/helpers";
import {
  getAccountNumberValidationSchema,
  getAmountValidationSchema,
  getBeneficiaryNameValidationSchema,
  getBooleanValidationSchema,
  getCardNumberValidationSchema,
  getCommissionAccountValidationSchema,
  getPurposeValidationSchema,
  getRequiredStringValidationSchema,
  getTinValidationSchema,
  getTransactionNumberValidationSchema,
} from "@/shared/validations";
import * as Yup from "yup";

const ALLOWED_REPORT_TYPE_CODES = ["AP", "EP", "FP", "GP", "LO", "OP", "OT", "RO", "TP", "YP", "ZD"];
const ALLOWED_REPORT_TYPES = ["HT", "GT", "TT", "OT"];

export const getMessageLineValidationSchema = ({ t }: ValidationSchemaProps) =>
  Yup.string()
    .nullable()
    .max(46, t(ErrorMessages.MaxMessageLine, { count: 46 }))
    .test(
      "starts-with-valid-prefix",
      t(ErrorMessages.MessageLineInvalidPrefix),
      (value) => {
        if (!value) return true;
        return MESSAGE_VALIDATION.some(prefix => value.startsWith(prefix));
      }
    )
    .test(
      "accepted-symbols",
      t(ErrorMessages.InvalidMessageLine),
      (value) => {
        if (!value) return true;
        return TEXT_FIELD_VALIDATION_REGEX.test(value);
      }
    );

export const getReportCodeValidationSchema = ({
  t, regionCode
}: ValidationSchemaReportCodeProps) =>
  Yup.string()
    .max(46, t("errorMessages.maxReportCode", { count: 46 }))
    .test(
      "starts-with-valid-prefix",
      t("errorMessages.reportCodeInvalidPrefix"),
      (value) => {
        if (!value) return true;
        return value.startsWith("PTD/");
      }
    )
    .test(
      "4th-5th-symbol-validation",
      t("errorMessages.reportCodeInvalidType"),
      (value) => {
        if (!value) return true;
        return ALLOWED_REPORT_TYPE_CODES.includes(value.slice(4, 6));
      }
    )
    .test(
      "6-st-symbol-validation",
      t("errorMessages.reportCodeStartDate"),
      (value) => {
        if (!value) return true;
        return value[6] === "M";
      }
    )
    .test(
      "7-12-digits-validation",
      t("errorMessages.reportCodeStartDateFormat"),
      (value) => {
        if (!value) return true;
        return /^\d{6}$/.test(value.slice(7, 13));
      }
    )
    .test(
      "13-st-symbol-validation",
      t("errorMessages.reportCodeEndDate"),
      (value) => {
        if (!value) return true;
        return value[13] === "E";
      }
    )
    .test(
      "14-19-digits-validation",
      t("errorMessages.reportCodeEndDateFormat"),
      (value) => {
        if (!value) return true;
        return /^\d{6}$/.test(value.slice(14, 20));
      }
    )
    .test(
      "20-21-symbol-validation",
      t("errorMessages.reportCodeInvalidReportType"),
      (value) => {
        if (!value) return true;
        return ALLOWED_REPORT_TYPES.includes(value.slice(20, 22));
      }
    )
    .test(
      "22-st-symbol-validation",
      t("errorMessages.reportCodeInvalidSlash"),
      (value) => {
        if (!value) return true;
        return value[22] === "/";
      }
    )
    .test(
      "23-st-symbol-validation",
      t("errorMessages.reportCodeRegionCode"),
      (value) => {
        if (!value || !regionCode) return true;
        return value.slice(23, 25) === regionCode;
      }
    )
    .test(
      "25-st-symbol-validation",
      t("errorMessages.reportCodeInvalidLastSlash"),
      (value) => {
        if (!value || value?.length <= 25) return true;
        return value.slice(25, 27) === "/0";
      }
    )
    .nullable();

export const ownAccountTransferValidationSchema = ({ t, isNameUnique }: ValidationSchemaProps) => {
  return Yup.object().shape({
    transactionNumber: getTransactionNumberValidationSchema({ t }),
    creditAccount: getRequiredStringValidationSchema({ t }),
    debitAccount: getRequiredStringValidationSchema({ t }),
    scheduleDate: Yup.string().required(t(ErrorMessages.RequiredField)),
    amount: getAmountValidationSchema({ t }),
    tin: getRequiredStringValidationSchema({ t }),
    purpose: getPurposeValidationSchema({ t }),
    templateName: Yup.string().when("isSaveAsTemplate", {
      is: Boolean,
      then: (schema) => schema.trim()
        .max(50, t(ErrorMessages.MaxInput, { count: 50 }))
        .required(t(ErrorMessages.RequiredField))
        .test(
          "name-unique",
          t("template.templateNameModal.templateNameNotUnique"),
          (value) => !value || isNameUnique !== false
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
};

export const accountTransferValidationSchema = ({
  t,
  accountsList,
  isAccountNumberInvalid,
  isNameUnique,
}: ValidationSchemaAccountTransferProps) =>
  Yup.object().shape({
    creditAccount: getAccountNumberValidationSchema({ t, isAccountNumberInvalid }),
    debitAccount: getRequiredStringValidationSchema({ t }),
    beneficiary: getBeneficiaryNameValidationSchema({ t }),
    amount: getAmountValidationSchema({ t }),
    tin: getRequiredStringValidationSchema({ t }),
    purpose: getPurposeValidationSchema({ t }),
    isUrgent: getBooleanValidationSchema(),
    beneficiaryLegalStatus: Yup.string().when("creditAccount", {
      is: (creditAccount?: string) => !isAmeriaAccount(creditAccount),
      then: (schema) => schema.required(t(ErrorMessages.RequiredField)),
      otherwise: (schema) => schema.notRequired(),
    }),
    templateName: Yup.string().when("isSaveAsTemplate", {
      is: Boolean,
      then: (schema) => schema.trim()
        .max(50, t(ErrorMessages.MaxInput, { count: 50 }))
        .required(t(ErrorMessages.RequiredField))
        .test(
          "name-unique",
          t("template.templateNameModal.templateNameNotUnique"),
          (value) => !value || isNameUnique,
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    transactionNumber: getTransactionNumberValidationSchema({ t }),
    scheduleDate: Yup.string().required(t(ErrorMessages.RequiredField)),
    commissionAccount: getCommissionAccountValidationSchema({ t, accountsList }),
  });

export const cardTransferValidationSchema = (
  { t, accountsList, isCardNumberInvalid, isNameUnique }: ValidationSchemaCardTransferProps) =>
  Yup.object().shape({
    transactionNumber: getTransactionNumberValidationSchema({ t }),
    commissionAccount: getCommissionAccountValidationSchema({ t, accountsList }),
    creditCardNumber: getCardNumberValidationSchema({ t })
      .test(
        "card-holder-valid",
        t(ErrorMessages.UnknownCardHolder),
        (value) => !value || value.length < 16 || !isCardNumberInvalid
      ),
    debitAccount: getRequiredStringValidationSchema({ t }),
    scheduleDate: Yup.string().required(t(ErrorMessages.RequiredField)),
    amount: getAmountValidationSchema({ t })
      .test(
        "min",
        t("errorMessages.amountMustBeGreater", { amount: CARD_TRANSFER_MIN_AMOUNT }),
        (value: string) => !value || Number(value) >= CARD_TRANSFER_MIN_AMOUNT
      )
      .test(
        "max",
        t("errorMessages.amountMustBeLess", { amount: CARD_TRANSFER_MAX_AMOUNT }),
        (value: string) => !value || Number(value) <= CARD_TRANSFER_MAX_AMOUNT
      ),
    purpose: getPurposeValidationSchema({ t }),
    templateName: Yup.string().when("isSaveAsTemplate", {
      is: Boolean,
      then: (schema) => schema.trim()
        .max(50, t(ErrorMessages.MaxInput, { count: 50 }))
        .required(t(ErrorMessages.RequiredField))
        .test(
          "name-unique",
          t("template.templateNameModal.templateNameNotUnique"),
          (value) => !value || isNameUnique !== false
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

export const budgetTransferValidationSchema = (
  {
    t, accountsList, isTinValid, isAccountNumberInvalid, isNameUnique
  }: ValidationSchemaBudgetTransferProps) =>
  Yup.object().shape({
    transactionNumber: getTransactionNumberValidationSchema({ t }),
    commissionAccount: getCommissionAccountValidationSchema({ t, accountsList }),
    creditBudgetAccount: getAccountNumberValidationSchema({ t, isAccountNumberInvalid }),
    debitAccount: getAccountNumberValidationSchema({ t }),
    scheduleDate: Yup.string().required(t(ErrorMessages.RequiredField)),
    amount: getAmountValidationSchema({ t }),
    tin: getTinValidationSchema({ t }).required(t(ErrorMessages.RequiredField)),
    purpose: getPurposeValidationSchema({ t }),
    isUrgent: getBooleanValidationSchema(),
    isAuthorizedTransfer: getBooleanValidationSchema(),
    regionCode: getRequiredStringValidationSchema({ t }),
    templateName: Yup.string().when("isSaveAsTemplate", {
      is: Boolean,
      then: (schema) => schema.trim()
        .max(50, t(ErrorMessages.MaxInput, { count: 50 }))
        .required(t(ErrorMessages.RequiredField))
        .test(
          "name-unique",
          t("template.templateNameModal.templateNameNotUnique"),
          (value) => !value || isNameUnique !== false
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    authorizedBudgetTransferParty: Yup.object().shape({})
      .when("isAuthorizedTransfer", ([isAuthorizedTransfer], schema) => {
        if (!isAuthorizedTransfer) {
          return schema.strip();
        }
        return schema.shape({
          fullName: Yup.string()
            .matches(
              TEXT_FIELD_VALIDATION_REGEX,
              { message: t(ErrorMessages.InvalidPurpose), excludeEmptyString: true }
            )
            .max(140, t("errorMessages.maxFullName"))
            .required(t(ErrorMessages.RequiredField)),
          residency: getRequiredStringValidationSchema({ t }),
          partyType: getRequiredStringValidationSchema({ t }),
          tin: Yup.string()
            .test("tin-valid", t(ErrorMessages.InvalidTin), () => isTinValid)
            .when(
              "partyType",
              ([partyType], schema) => {
                return partyType !== PartyType.NaturalPerson
                  ? schema
                    .matches(ONLY_DIGITS, t("errorMessages.onlyDigits"))
                    .length(8, t("errorMessages.tinInvalidLength", { length: 8 }))
                    .required(t(ErrorMessages.RequiredField))
                  : schema.strip();
              }
            ),
          ssn: Yup.string()
            .when(
              "partyType",
              ([partyType], schema) => {
                return partyType === PartyType.NaturalPerson ?
                  schema
                    .matches(ONLY_DIGITS, t("errorMessages.onlyDigits"))
                    .length(10, t(ErrorMessages.SsnInvalidLength, { length: 10 }))
                    .required(t(ErrorMessages.RequiredField))
                  : schema.strip();
              }
            ),
          documentType: Yup.string()
            .when(
              ["partyType", "residency"],
              ([partyType, residency], schema) =>
                partyType === PartyType.NaturalPerson && residency === ResidenceType.NonResident
                  ? schema.required(t(ErrorMessages.RequiredField))
                  : schema.nullable()
            ),
          documentNumber: Yup.string()
            .matches(
              TEXT_FIELD_VALIDATION_REGEX,
              { message: t(ErrorMessages.InvalidDocumentNumber), excludeEmptyString: true }
            )
            .max(100, t("errorMessages.maxDocumentNumber", { count: 100 }))
            .when("documentType", ([documentType], schema) =>
              documentType ? schema.required(t(ErrorMessages.RequiredField)) : schema.nullable()
            ),
          address: Yup.string()
            .nullable()
            .matches(
              TEXT_FIELD_VALIDATION_REGEX,
              { message: t(ErrorMessages.InvalidPurpose), excludeEmptyString: true }
            )
            .max(33, t("errorMessages.maxAddress", { count: 33 })),
          additionalInfo: Yup.string()
            .nullable()
            .matches(
              TEXT_FIELD_VALIDATION_REGEX,
              { message: t(ErrorMessages.InvalidPurpose), excludeEmptyString: true }
            )
            .max(33, t("errorMessages.maxAdditionalInfo", { count: 33 })),
          authorizedPerson: Yup.string()
            .nullable()
            .matches(
              TEXT_FIELD_VALIDATION_REGEX,
              { message: t(ErrorMessages.InvalidPurpose), excludeEmptyString: true }
            )
            .max(33, t("errorMessages.maxAuthorizedPerson", { count: 33 })),
        });
      }),
    messages: Yup.object()
      .when("regionCode", ([regionCode], schema) =>
        schema.shape({
          reportCode: getReportCodeValidationSchema({ t, regionCode }),
          message1: getMessageLineValidationSchema({ t }),
          message2: getMessageLineValidationSchema({ t }),
          message3: getMessageLineValidationSchema({ t }),
          message4: getMessageLineValidationSchema({ t }),
          message5: getMessageLineValidationSchema({ t }),
          message6: getMessageLineValidationSchema({ t }),
        })
      ),
  });

export const currencyExchangeValidationSchema = ({ t, isNameUnique }: ValidationSchemaProps) => {
  return Yup.object().shape({
    transactionNumber: getTransactionNumberValidationSchema({ t }),
    creditAccount: getRequiredStringValidationSchema({ t }),
    debitAccount: getRequiredStringValidationSchema({ t }),
    amount: getAmountValidationSchema({ t }),
    wantAmount: getRequiredStringValidationSchema({ t })
      .transform(value => cleanupAmount(value, ""))
      .test("amountValidation", t(ErrorMessages.MinAmount), (value) => +value > 0),
    preferentialRate: Yup.string()
      .transform(value => cleanupAmount(value, ""))
      .test("maxAmount", t(ErrorMessages.MaxAmount, { count: MAX_AMOUNT }), (value) => {
        if (!value) return true;
        return +value <= MAX_AMOUNT;
      }),
    tin: getRequiredStringValidationSchema({ t }),
    purpose: getPurposeValidationSchema({ t }),
    templateName: Yup.string().when("isSaveAsTemplate", {
      is: Boolean,
      then: (schema) => schema.trim()
        .max(50, t(ErrorMessages.MaxInput, { count: 50 }))
        .required(t(ErrorMessages.RequiredField))
        .test(
          "name-unique",
          t("template.templateNameModal.templateNameNotUnique"),
          (value) => !value || isNameUnique !== false
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
};

export const beneficiariesValidationSchema = ({ t }: ValidationSchemaProps) => Yup.object({
  receiverAccount: getAccountNumberValidationSchema({ t }),
  receiverName: getRequiredStringValidationSchema({ t })
    .max(
      INPUT_MAX_LIMIT,
      t("errorMessages.maxBeneficiaryName", { count: INPUT_MAX_LIMIT })
    ),
  amount: getAmountValidationSchema({ t }),
});

export const groupTransferValidationSchema = (
  { t, accountsList }: ValidationSchemaGroupTransferProps) =>
  Yup.object().shape({
    transactionNumber: getTransactionNumberValidationSchema({ t }),
    commissionAccount: getCommissionAccountValidationSchema({ t, accountsList }),
    debitAccount: getRequiredStringValidationSchema({ t }),
    beneficiaries: Yup.array().of(beneficiariesValidationSchema({ t })),
    scheduleDate: Yup.string().required(t(ErrorMessages.RequiredField)),
    purpose: getPurposeValidationSchema({ t }),
  });

export const addEditBeneficiaryValidationSchema = (
  {
    t,
    isEdit,
    ownAccounts,
    selectedRowAccount,
    isAccountNumberInvalid,
    groupTransferBeneficiariesList,
  }: ValidationSchemaBeneficiaryListProps) =>
  Yup.object().shape({
    account: getAccountNumberValidationSchema({ t })
      .test(
        "no-9000-prefix",
        t("errorMessages.budgetAccount"),
        (value) => !value?.startsWith(BUDGET_ACCOUNT_PREFIX)
      )
      .test(
        "not-own-account",
        t("errorMessages.ownAccount"),
        (value) => !ownAccounts.includes(value ?? "")
      )
      .test(
        "already-exists",
        t("errorMessages.alreadyExists"),
        (value) => {
          if (isEdit && selectedRowAccount === value) {
            return true;
          }
          return !groupTransferBeneficiariesList.some((el) => el.receiverAccount === value);
        }
      )
      .test(
        "different-bank",
        t("errorMessages.differentBank"),
        (value) => {
          if (isEdit && groupTransferBeneficiariesList.length === 1) {
            return true;
          }
          const accountPrefix = value.slice(0, 2);
          const isSameBank = !!groupTransferBeneficiariesList.find((el) => el.receiverAccount.slice(
            0,
            2
          ) === accountPrefix);
          return isSameBank || !groupTransferBeneficiariesList?.length;
        }
      )
      .test(
        "account-holder-valid",
        t(ErrorMessages.UnknownAccountHolder),
        () => !isAccountNumberInvalid
      ),
    beneficiaryLegalStatus: Yup.string().when("account", {
      is: (account?: string) => !isAmeriaAccount(account),
      then: (schema) => schema.required(t(ErrorMessages.RequiredField)),
      otherwise: (schema) => schema.notRequired(),
    }),
    beneficiary: getBeneficiaryNameValidationSchema({ t }),
    amount: getAmountValidationSchema({ t }),
  });

export const loanRepaymentTransferValidationSchema = (
  { t, isNameUnique }: ValidationSchemaProps) =>
  Yup.object().shape({
    loanContractNumber: Yup.string().required(t(ErrorMessages.RequiredField)),
    loanRepaymentType: Yup.string().required(t(ErrorMessages.RequiredField)),
    transactionNumber: getTransactionNumberValidationSchema({ t }),
    debitAccount: getRequiredStringValidationSchema({ t }),
    amount: getAmountValidationSchema({ t }),
    templateName: Yup.string().when("isSaveAsTemplate", {
      is: Boolean,
      then: (schema) => schema.trim()
        .max(50, t(ErrorMessages.MaxInput, { count: 50 }))
        .required(t(ErrorMessages.RequiredField))
        .test(
          "name-unique",
          t("template.templateNameModal.templateNameNotUnique"),
          (value) => !value || isNameUnique !== false
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
